import { tableAnatomy as parts } from '@chakra-ui/anatomy';
import { defineStyleConfig, theme } from '@chakra-ui/react';
import { createMultiStyleConfigHelpers } from '@chakra-ui/styled-system';

const { defineMultiStyleConfig } = createMultiStyleConfigHelpers(parts.keys);

const Table = defineMultiStyleConfig({
  variants: {
    primary: ({ colorMode }) => {
      const isDark = colorMode === 'dark';

      return {
        th: {
          textTransform: 'initial',
        },
        td: {
          borderWidth: 1,
          bg: isDark ? 'gray.600' : 'gray.100',
          p: 0,
        },
      };
    },
    sensible: () => {
      return {
        table: {
          borderCollapse: 'separate',
          background: 'gray.200',
          borderRadius: '0.5em',
          _dark: {
            background: 'gray.600',
          },
        },
        td: {
          background: 'white',
          _dark: {
            background: 'gray.700',
          },
        },
      };
    },
    compact: () => {
      return {
        table: {
          borderCollapse: 'separate',
          background: 'gray.200',
          borderRadius: '0.5em',
          _dark: {
            background: 'gray.600',
          },
        },
        th: {
          px: 2,
          py: 0,
          background: 'gray.200',
          _dark: {
            background: 'gray.700',
          },
        },
        td: {
          px: 2,
          py: 0,
          background: 'white',
          _dark: {
            background: 'gray.700',
          },
        },
      };
    },
    // Please...
    extraCompact: () => ({
      td: {
        p: 0,
      },
      th: {
        p: 0,
      },
    }),
  },
});

const Input = defineStyleConfig({
  baseStyle: {
    size: 'md',
    field: {
      _hover: {
        borderColor: 'accent',
      },
      _focus: {
        borderColor: 'accent !important',
      },
      _active: {
        borderColor: 'accent',
      },
    },
  },
});

const Select = defineStyleConfig({
  baseStyle: {
    size: 'md',
    field: {
      px: 4,
      _hover: {
        borderColor: 'accent',
      },
      _focus: {
        borderColor: 'accent !important',
      },
      _active: {
        borderColor: 'accent',
      },
      _invalid: {
        borderColor: 'error',
      },
    },
  },
  variants: {
    autoWidth: (...rest) => {
      // I'm not changing this variable name as a monument to how I feel
      // This initializes a default variant and passes in everything, except for a single attribute in the `field` component
      const suffering = theme.components.Select.variants.filled(...rest);
      return {
        ...suffering,
        field: {
          ...suffering.field,
          width: 'auto',
        },
      };
    },
  },
});

const Textarea = defineStyleConfig({
  baseStyle: {
    size: 'md',
    _hover: {
      borderColor: 'accent !important',
    },
    _focus: {
      borderColor: 'accent !important',
    },
    _active: {
      borderColor: 'accent',
    },
  },
});

const Drawer = defineStyleConfig({
  sizes: {
    xxs: { dialog: { maxH: '100%', maxW: '35%' } },
    xs: { dialog: { maxH: '100%', maxW: '50%' } },
    s: { dialog: { maxH: '100%', maxW: '70%' } },
    '2xl': { dialog: { maxH: '100%', maxW: '80%' } },
    '3xl': { dialog: { maxH: '100%', maxW: '90%' } },
    '4xl': { dialog: { maxH: '100%', maxW: '95%' } },
    '7xl': { dialog: { maxW: 'var(--chakra-sizes-7xl)' } },
  },
});

const Popover = defineStyleConfig({
  baseStyle: {
    popper: {
      width: 'fit-content',
      maxWidth: 'fit-content',
    },
    content: {
      width: 'fit-content',
      maxWidth: 'fit-content',
    },
  },

  variants: {
    high: {
      popper: {
        zIndex: 10,
      },
    },
    low: {
      popper: {
        zIndex: -10,
      },
    },
  },

  sizes: {
    xxl: {
      popper: {},
    },
  },
});

const Menu = defineStyleConfig({
  baseStyle: {
    item: {
      pt: 2.5,
      pb: 2.5,
    },
  },
});

const Checkbox = defineStyleConfig({
  baseStyle: {
    control: {},
  },
  sizes: {
    xl: {
      control: {
        w: '1.5em',
        h: '1.5em',
      },
    },
  },
  defaultProps: {
    size: 'xl',
    colorScheme: 'accent',
  },
});

export { default as Heading } from './heading';
export { Checkbox, Drawer, Input, Menu, Popover, Select, Table, Textarea };
